import { metaDefinida } from "../../utils/meta";
// Lógica pura extraída de index.jsx (componente de ~3300 linhas, tudo dentro
// de um único IIFE de render). Extraída pra ser testável sem precisar montar
// o componente inteiro (Provider + ctx com ~30 props + múltiplos children
// pesados). Mantém o mesmo comportamento das expressões originais — ver os
// comentários de cada função apontando a linha correspondente em index.jsx.
//
// NOTA (bug corrigido): index.jsx chamava `safeGanhoPp(a.sucesso.ganhoPp)`
// em 5 lugares (modal de "Sucessos") sem nunca importar essa função,
// lançando `ReferenceError: safeGanhoPp is not defined` assim que o usuário
// abria essa tela com ao menos uma ação com `sucesso` registrado. Corrigido
// importando o `safeGanhoPp` definido abaixo em index.jsx.

/**
 * Filtra a lista de ações do plano de ação pelos filtros de status,
 * responsável e busca por título. Espelha o `.filter` em index.jsx
 * (por volta da linha 277).
 */
export function filtrarAcoes(apItems, { filtroSt, filtroResp, busca, todayStr }) {
  return apItems.filter((a) => {
    const stEmpty = filtroSt.size === 0;
    if (stEmpty && a.status === "arquivado") return false;
    if (!stEmpty) {
      let match = false;
      for (const s of filtroSt) {
        if (s === "concluido_sucesso") {
          if (a.status === "concluido" && a.sucesso) {
            match = true;
            break;
          }
        } else if (s === "concluido_geral") {
          if (a.status === "concluido") {
            match = true;
            break;
          }
        } else if (s === "atrasado") {
          const hojeStr = todayStr();
          if (
            a.prazo &&
            a.prazo < hojeStr &&
            a.status !== "concluido" &&
            a.status !== "arquivado"
          ) {
            match = true;
            break;
          }
        } else if (a.status === s) {
          match = true;
          break;
        }
      }
      if (!match) return false;
    }
    if (filtroResp.size > 0 && !filtroResp.has(a.resp)) return false;
    if (busca && !a.titulo.toLowerCase().includes(busca.toLowerCase()))
      return false;
    return true;
  });
}

/**
 * Texto + cor de "saldo" (atual - meta), usado pelo index.jsx. Sem meta
 * cadastrada (0, ver utils/meta.js) não há saldo: antes NPS 41 contra meta 0
 * aparecia como "+41 pts" em verde.
 */
export function calcSaldo(atual, meta, isNps, C) {
  if (metaDefinida(meta) === null) return { texto: "-", cor: C.text4, delta: 0 };
  const delta = atual - meta;
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
  const unit = isNps ? "pts" : "pp";
  const cor =
    delta === 0
      ? C.text4
      : delta > 0
        ? C.green
        : delta >= -10
          ? C.orange
          : C.red;
  return { texto: `${sign}${Math.abs(delta)} ${unit}`, cor, delta };
}

/**
 * Preview do bulk-update de metas (aplica +pct% sobre o valor atual).
 * Espelha `previewBulk` em index.jsx (por volta da linha 356).
 * Retorna null se `bulkPct` não é um número válido.
 */
export function calcPreviewBulk({
  bulkPct,
  valoresAtuais,
  JCFG,
  valorEtapaAtual,
  bulkApplyGerais,
  bulkApplyEtapas,
}) {
  const pct = parseFloat(bulkPct);
  if (isNaN(pct)) return null;
  const fator = 1 + pct / 100;
  const novasGerais = {
    jcx: Math.round(valoresAtuais.jcx * fator),
    nps: Math.round(valoresAtuais.nps * fator),
    csat: Math.round(valoresAtuais.csat * fator),
    ces: Math.round(valoresAtuais.ces * fator),
  };
  const novasEtapas = {};
  Object.keys(JCFG).forEach((nome) => {
    novasEtapas[nome] = Math.round(valorEtapaAtual(nome) * fator);
  });
  const totalAfetadas = (bulkApplyGerais ? 4 : 0) + (bulkApplyEtapas ? 11 : 0);
  return { novasGerais, novasEtapas, totalAfetadas, fator };
}

/**
 * Nível de gamificação (Iniciante..Elite) + progresso pro próximo nível,
 * dado o total de pp acumulado. Espelha `getSucessoStats` em index.jsx
 * (por volta da linha 68-80), isolando só a parte de cálculo de nível
 * (a parte de `getAchievementStats` já vive em dashboard/_shared e não foi
 * duplicada aqui).
 */
export const LEVELS = [
  { id: 1, name: "Iniciante", icon: "🌱", color: "#6B7280", bg: "#F3F4F6", min: 0 },
  { id: 2, name: "Intermediário", icon: "⭐", color: "#F59E0B", bg: "#FFFBEB", min: 10 },
  { id: 3, name: "Avançado", icon: "🏆", color: "#10B981", bg: "#ECFDF5", min: 30 },
  { id: 4, name: "Expert", icon: "💎", color: "#6366F1", bg: "#EEF2FF", min: 60 },
  { id: 5, name: "Elite", icon: "🚀", color: "#EF4444", bg: "#FEF2F2", min: 100 },
];

export function calcularNivelGamificacao(totalPp, levels = LEVELS) {
  const level = [...levels].reverse().find((l) => totalPp >= l.min) || levels[0];
  const nextLevel = levels.find((l) => l.min > totalPp) || null;
  const progressPct = nextLevel
    ? Math.round(((totalPp - level.min) / (nextLevel.min - level.min)) * 100)
    : 100;
  return { level, nextLevel, progressPct };
}

/** Coerção defensiva de ganhoPp — ver nota de bug no topo do arquivo. */
export function safeGanhoPp(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Filtra ações com sucesso registrado pelo período (todos|3m|6m|12m), com
 * base em `sucesso.registradoEm`. Espelha o bloco em index.jsx por volta
 * da linha 2660-2678. `agora` é injetável para testes determinísticos.
 */
export function filtrarSucessosPorPeriodo(acoes, periodo, agora = new Date()) {
  const todasComSucesso = acoes.filter((a) => a.sucesso);
  const filtroEm = (a) => {
    if (periodo === "todos") return true;
    const dataReg = a.sucesso?.registradoEm ? new Date(a.sucesso.registradoEm) : null;
    if (!dataReg) return periodo === "todos";
    const diffMeses = (agora - dataReg) / (1000 * 60 * 60 * 24 * 30);
    if (periodo === "3m") return diffMeses <= 3;
    if (periodo === "6m") return diffMeses <= 6;
    if (periodo === "12m") return diffMeses <= 12;
    return true;
  };
  return todasComSucesso.filter(filtroEm);
}

/**
 * Agrega sucessos filtrados por etapa (count, totalPp, lista de ações),
 * ordenado por totalPp desc. Espelha o bloco em index.jsx por volta da
 * linha 2690-2701.
 */
export function agruparSucessoPorEtapa(sucessosFiltrados) {
  const porEtapa = {};
  sucessosFiltrados.forEach((a) => {
    const et = a.etapa || "Outras";
    if (!porEtapa[et]) porEtapa[et] = { etapa: et, count: 0, totalPp: 0, acoes: [] };
    porEtapa[et].count += 1;
    porEtapa[et].totalPp += safeGanhoPp(a.sucesso.ganhoPp);
    porEtapa[et].acoes.push(a);
  });
  return Object.values(porEtapa).sort((a, b) => b.totalPp - a.totalPp);
}

/**
 * Evolução cumulativa de pp ganhos ao longo do tempo, ordenada por data de
 * registro. Espelha o bloco em index.jsx por volta da linha 2703-2722.
 */
export function calcularEvolucaoCumulativa(sucessosFiltrados) {
  const ordenadosPorData = [...sucessosFiltrados]
    .filter((a) => a.sucesso?.registradoEm)
    .sort((a, b) => new Date(a.sucesso.registradoEm) - new Date(b.sucesso.registradoEm));
  let acumulado = 0;
  return ordenadosPorData.map((a) => {
    acumulado += safeGanhoPp(a.sucesso.ganhoPp);
    const d = new Date(a.sucesso.registradoEm);
    return {
      data: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`,
      dataFull: a.sucesso.registradoEm,
      acumulado,
      ganho: safeGanhoPp(a.sucesso.ganhoPp),
      titulo: a.titulo,
      etapa: a.etapa,
    };
  });
}
